import {
  DataBlockFieldDto,
  DataBlockTableColumnDto,
  DataType,
  PatchPatientDto,
  PatientListDto,
  NumberSource,
  PatientDto,
  PatientEgkCardData,
} from '@lmt/shared-types';
import { getFormattedDate } from '@lmt/unicorn-shared/app/components/patients/common';

export enum FieldStatus {
  firstPatient = 'firstPatient',
  secondPatient = 'secondPatient',
  notSelected = 'notSelected',
  const = 'const',
}

export enum FieldType {
  property = 'property',
  customField = 'customField',
}

export interface CustomFieldProps {
  value: any;
  dataType?: DataType;
  dictionaryId?: number;
  columns?: DataBlockTableColumnDto[];
}

export type PatientData = PatientDto & {
  title?: number | string;
  healthInsuranceCompany?: string;
};

export function getContacts(patient: PatientDto): string {
  return [
    patient.mobilePhone,
    patient.homePhone,
    patient.workPhone,
    patient.email,
    patient.fax,
  ]
    .filter((contact) => !!contact)
    .join(' \n');
}

export function makeSelectedPatientTitle(
  selectedPatient: PatientListDto | string,
): string {
  if (typeof selectedPatient === 'string' || selectedPatient == null) return '';

  return `${selectedPatient.firstName} ${
    selectedPatient.lastName
  } ${getFormattedDate(selectedPatient.dateOfBirth)}`;
}

export function getPatientDict(patient?: PatientData | PatientEgkCardData): {
  [key: string]: any;
} {
  const patientDict: { [key: string]: any } = {};
  if (patient) {
    Object.entries(patient).forEach((item) => (patientDict[item[0]] = item[1]));
  }
  return patientDict;
}

const filter = [
  'values',
  'id',
  'numberSource',
  'isDeleted',
  'comments',
  'subtenantId',
  'tagId',
  'titleId',
  'priorityContact',
  'priorityContactType',
  'importErrors',
  'lastChangeDateTime',
  'tags',
  'importId',
];

// patient title custom field key
export const titleFieldKey = 'Title';

export function isEmpty(value: any) {
  return !value || value.toString().trim().length <= 0;
}

export function getPatientFields(
  patient1Dict: { [key: string]: any },
  patient2Dict: { [key: string]: any },
): { [key: string]: FieldStatus } {
  const fields: { [key: string]: FieldStatus } = {};
  Object.keys(patient1Dict).forEach((key) => {
    const value1 = patient1Dict[key];
    const value2 = patient2Dict[key];
    if (filter.includes(key)) {
      fields[key] = FieldStatus.const;
    } else if (value1 === value2 || (isEmpty(value1) && isEmpty(value2))) {
      fields[key] = FieldStatus.const;
    } else if (value1 && isEmpty(value2)) {
      fields[key] = FieldStatus.firstPatient;
    } else if (value2 && isEmpty(value1)) {
      fields[key] = FieldStatus.secondPatient;
    } else {
      fields[key] = FieldStatus.notSelected;
    }
    if (key === 'dateOfBirth' && value1 && value2) {
      if ((value1 as Date).toDateString() === (value2 as Date).toDateString()) {
        fields[key] = FieldStatus.const;
      }
    }
    if (key === 'number') {
      if (
        value1 === value2 &&
        patient1Dict['numberSource'] !== patient2Dict['numberSource']
      ) {
        fields[key] = FieldStatus.notSelected;
      }
    }
  });
  return fields;
}

export function getPatientCustomFields(
  firstPatientCustomFields: { [key: string]: any },
  secondPatientCustomFields: { [key: string]: any },
  patientSharedFields: DataBlockFieldDto[],
): { [key: string]: FieldStatus } {
  const customFields: { [key: string]: FieldStatus } = {};
  patientSharedFields
    .filter((x) => x.name !== 'Sex')
    .forEach((field) => {
      const key = field.name;
      const value1 = firstPatientCustomFields[key];
      const value2 = secondPatientCustomFields[key];
      if (isEmpty(value1) && isEmpty(value2)) {
      } else if (value1 && isEmpty(value2)) {
        customFields[key] = FieldStatus.firstPatient;
      } else if (isEmpty(value1) && value2) {
        customFields[key] = FieldStatus.secondPatient;
      } else if (value1 === value2) {
        customFields[key] = FieldStatus.const;
      } else {
        customFields[key] = FieldStatus.notSelected;
      }
    });
  return customFields;
}

interface GetUpdatedPatientProps {
  fields: { [key: string]: FieldStatus };
  customFields: { [key: string]: FieldStatus };
  patient1Dict: { [key: string]: any };
  patient2Dict: { [key: string]: any };
  patient1CustomFields: { [key: string]: any };
  patient2CustomFields: { [key: string]: any };
}

export function getUpdatedPatient(
  props: GetUpdatedPatientProps,
): PatchPatientDto {
  const {
    fields,
    customFields,
    patient1Dict,
    patient2Dict,
    patient1CustomFields,
    patient2CustomFields,
  } = props;
  function getPatientValue(field: string) {
    const status = fields[field];
    if (status === FieldStatus.firstPatient) {
      return patient1Dict[field];
    }
    if (status === FieldStatus.secondPatient) {
      return patient2Dict[field];
    }
    return undefined;
  }

  function getPatientCustomValue(field: string) {
    const status = customFields[field];
    if (status === FieldStatus.firstPatient) {
      return patient1CustomFields[field];
    }
    if (status === FieldStatus.secondPatient) {
      return patient2CustomFields[field];
    }
    if (status === FieldStatus.const) {
      return patient1CustomFields[field];
    }
    return undefined;
  }

  const updatedPatientDict: any[][] = [];
  Object.entries(props.fields)
    .filter(
      (itm) =>
        itm[1] === FieldStatus.firstPatient ||
        itm[1] === FieldStatus.secondPatient,
    )
    .forEach((itm) =>
      updatedPatientDict.push([itm[0], getPatientValue(itm[0])]),
    );
  const updatedPatientCustomFields: { [key: string]: any } = {};

  Object.entries(props.customFields)
    .filter((itm) => itm[1] !== FieldStatus.notSelected)
    .forEach(
      (itm) =>
        (updatedPatientCustomFields[itm[0]] = getPatientCustomValue(itm[0])),
    );

  if (Object.keys(updatedPatientCustomFields).length) {
    updatedPatientDict.push(['values', updatedPatientCustomFields]);
  }

  const updatedPatient: PatchPatientDto =
    Object.fromEntries(updatedPatientDict);
  return updatedPatient;
}

export function getNumberPostfix(
  numberSource: NumberSource,
  postFix: string,
): string {
  return numberSource === NumberSource.External ? postFix : '';
}
